import {useAutoAnimate} from '@formkit/auto-animate/react';
import cx from 'classnames';
import moment from 'moment-timezone';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {AddMultipleSlots} from '~/SignUpSheets/Create/routes/Customize/components/AddMultipleSlots/AddMultipleSlots';
import {SlotToolBar} from '~/SignUpSheets/Create/routes/Customize/components/Builder/Elements/SlotToolBar/SlotToolBar';
import {AddButtonWithTitle} from '~/SignUpSheets/components/AddButtonWithTitle/AddButtonWithTitle';
import {DetailsToggle} from '~/SignUpSheets/components/DetailsToggle/DetailsToggle';
import {FilledSlots} from '~/SignUpSheets/components/FilledSlots/FilledSlots';
import {RemoveActiveSlotModal} from '~/SignUpSheets/components/LeaveModal/LeaveModal';
import {useSortTimes} from '~/SignUpSheets/hooks/useSortTimes';
import {useUnlockGate} from '~/SignUpSheets/hooks/useUnlockGate';
import {
  selectExpired,
  selectGateUnlockedForSheet,
  selectIsInEditMode,
  selectSignupsBySlotId,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {selectSlotColor} from '~/SignUpSheets/reducers/themes/selectors';
import {
  entryHasProductLink,
  getTime30MinsLater,
  isAllDay,
  resizeTextarea,
  signupOptionIsAfterToday,
  signupOptionIsBeforeToday,
  signupOptionIsToday,
  slotElapsed,
} from '~/SignUpSheets/utils/misc';
import {initializeSlot} from '~/SignUpSheets/utils/schema';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsMediumAndUp} from '~library/hooks/useMatchQuery';
import {Dropdown} from '~library/molecules/Dropdown';
import {GatedContent} from '~library/molecules/GatedContent';
import {LinkField} from './LinkField';
import {NumberField} from './NumberField';
import {RichTextField} from './RichTextField';
import {TextField} from './TextField';
import {TimesField} from './TimesField';

import './SlotsField.sass';

const Slot = ({
  slot: {
    start_time: startTime,
    end_time: endTime,
    slot_id: slotId,
    quantity,
    description = '',
    title,
    pasted_from_all_day: pastedFromAllDay = false,
    product_link: productLink,
    pristine_quantity: pristineQuantity = false,
  },
  currentUserId,
  index,
  onDuplicate,
  onChange,
  onHandleProductLinkBlur,
  onDelete,
  dataQaId,
  schema,
  length,
  disabled = false,
  errors,
  defaultStart = pastedFromAllDay ? null : schema.placeholder[0],
  defaultEnd = pastedFromAllDay ? null : schema.placeholder[1],
  linkPreview = {
    // biome-ignore lint/style/useNamingConvention: existing error
    affiliate_url: '',
    images: {
      primary: {
        small: '',
        medium: '',
        large: '',
      },
    },
    title: null,
    price: {
      // biome-ignore lint/style/useNamingConvention: existing error
      display_amount: '',
    },
    // biome-ignore lint/style/useNamingConvention: existing error
    vendor_name: null,
    url: '',
  },
  newOption = false,
  minQuantity = 1,
  blueprintType,
  upcoming = true,
  allDay: hideTimeSlots,
  copied = false,
  isDesktop = false,
}) => {
  const isMedium = useIsMediumAndUp();
  const descriptionRef = useRef(null);
  const wasInitiallyAllDay = useRef(hideTimeSlots && index === 0);
  const slotsFieldTimesRef = useRef();
  const [removeCallback, setRemoveCallback] = useState(null);
  const isInEditMode = useSelector(selectIsInEditMode);
  const signedUpTimeSlots = useSelector(selectSignupsBySlotId(slotId), shallowEqual);
  const allDay = isAllDay(startTime, endTime);

  const minCount = useMemo(
    () => Math.max(minQuantity, signedUpTimeSlots.length),
    [minQuantity, signedUpTimeSlots.length]
  );

  const [timeSlotAnimation] = useAutoAnimate({
    duration: 250,
    easing: 'ease-in-out',
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const warning = useMemo(() => {
    if (!startTime || !endTime) return null;
    const start = moment(startTime, 'hh:mm A'); // Create date object based on time; exact date used doesn't matter
    const end = moment(endTime, 'hh:mm A'); // Create date object based on time; exact date used doesn't matter
    if (end.isBefore(start)) {
      // End time is "before" start time, a.k.a. next day
      end.add(1, 'day');
    }
    const diffHours = end.diff(start, 'hours', true);
    start.hour(0).minute(0); // Set hours and minutes to 0 on original date objects to check diff days
    end.hour(0).minute(0); // Set hours and minutes to 0 on original date objects to check diff days
    const diffDays = end.diff(start, 'days'); // Check if end time is on the next day as the start time
    const diffHoursStr = Number.isInteger(diffHours)
      ? diffHours.toString()
      : `over ${Math.floor(diffHours)}`;
    if (diffHours > 12 && diffDays >= 1) {
      // Slot is >12 hours long and ends on the next day
      return `Heads up: this slot is ${diffHoursStr} hours long and ends on the next calendar day`;
    }
    if (diffDays >= 1 && !hideTimeSlots) {
      // Slot ends on the next day
      return `Heads up: this end time falls on the next calendar day`;
    }
    return null;
  }, [startTime, endTime]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onDeleteClick = useCallback(() => {
    if (disabled) {
      setRemoveCallback(() => () => {
        onDelete();
        setRemoveCallback(null);
      });
    } else {
      onDelete();
    }
  }, [disabled, onDelete, setRemoveCallback]);

  const onChangeStartTime = useCallback(
    (time) => {
      // biome-ignore lint/style/useNamingConvention: existing error
      onChange({start_time: time, end_time: endTime || null});
    },
    [onChange, endTime]
  );

  const onChangeEndTime = useCallback(
    (time) => {
      // biome-ignore lint/style/useNamingConvention: existing error
      onChange({end_time: time, start_time: startTime || null});
    },
    [onChange, startTime]
  );

  const onQuantityChange = useCallback(
    (newValue, saveChanges = true) => {
      onChange(
        {
          quantity: newValue ? parseInt(newValue, 10) : newValue,
          // biome-ignore lint/style/useNamingConvention: existing error
          pristine_quantity: false,
        },
        saveChanges
      );
    },
    [onChange]
  );

  const onChangeSignupTitle = useCallback(
    (newValue) => {
      onChange({title: newValue});
    },
    [onChange]
  );

  const onDescriptionChange = useCallback(
    (newValue) => {
      onChange({description: newValue});
    },
    [onChange]
  );

  const onProductLinkChange = useCallback(
    (newValue, saveChanges = true) => {
      // biome-ignore lint/style/useNamingConvention: existing error
      onChange({product_link: {...(productLink ?? {}), ...newValue}}, saveChanges);
    },
    [onChange, productLink]
  );

  useEffect(() => {
    resizeTextarea(descriptionRef.current);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (!hideTimeSlots && wasInitiallyAllDay.current && allDay) {
      const anHourFromCurrentTime = moment().add(1, 'hours');
      const remainder =
        anHourFromCurrentTime.minute() % 5 === 0
          ? anHourFromCurrentTime.minute() % 5
          : 5 - (anHourFromCurrentTime.minute() % 5);
      const roundedDefaultStart = anHourFromCurrentTime.add(remainder, 'minutes').format('h:mm A');
      onChange({
        // biome-ignore lint/style/useNamingConvention: existing error
        start_time: roundedDefaultStart,
        // biome-ignore lint/style/useNamingConvention: existing error
        end_time: getTime30MinsLater(roundedDefaultStart),
      });
      wasInitiallyAllDay.current = false;
    }
  }, [hideTimeSlots, allDay]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const SlotDescription = useMemo(() => {
    if (!schema.time_slot_has_description) return null;

    return (
      <RichTextField
        id={`${schema.name}-${slotId}-description`}
        label={schema.time_slot_description_display_name}
        value={description}
        onChange={onDescriptionChange}
        placeholder={schema.time_slot_description_placeholder}
        maxLength={schema.time_slot_description_max_chars}
        dataQaId={`${dataQaId}-description`}
      />
    );
  }, [schema, description, onDescriptionChange, dataQaId, resizeTextarea]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const QuantityCounter = useMemo(
    () =>
      schema.time_slot_has_quantity ? (
        <NumberField
          schema={schema}
          value={quantity}
          onChange={onQuantityChange}
          errors={errors.filter((e) => e.loc?.[4] === 'quantity')}
          disabled={disabled || !upcoming}
          min={minCount}
          max={schema.time_slot_quantity_max}
          data-qa-id={`${dataQaId}-quantity`}
          isDesktop={isDesktop}
          pristine={pristineQuantity}
        />
      ) : null,
    [schema, quantity, onQuantityChange, errors, disabled, isDesktop, minCount, upcoming, dataQaId]
  );

  const SlotProductLink = useMemo(
    () =>
      schema.time_slot_has_product_link ? (
        <LinkField
          onChange={onProductLinkChange}
          onBlur={onHandleProductLinkBlur}
          copied={copied}
          schema={{
            name: 'slots-field__product-link',
            // biome-ignore lint/style/useNamingConvention: existing error
            display_name: schema.time_slot_product_link_display_name,
            placeholder: schema.time_slot_product_link_placeholder,
            // biome-ignore lint/style/useNamingConvention: existing error
            max_chars: schema.time_slot_product_link_max_chars,
            // biome-ignore lint/style/useNamingConvention: existing error
            label_adornment: schema.time_slot_product_link_label_adornment,
          }}
          previewContainerClassName={cx({
            'slots-field__link-preview-wrapper__create-edit': signedUpTimeSlots.length === 0,
          })}
          className="slots-field__slot-product-link"
          isNew={newOption}
          value={productLink}
          errors={errors.filter((e) => e.loc?.[4] === 'product_link')}
          isInEditMode={isInEditMode}
          preview={linkPreview}
          data-qa-id={`${dataQaId}-product-link`}
          uuid={slotId}
          disabled={disabled}
        />
      ) : null,
    [
      schema,
      onProductLinkChange,
      onHandleProductLinkBlur,
      newOption,
      productLink,
      errors,
      isInEditMode,
      linkPreview,
      signedUpTimeSlots.length,
      dataQaId,
      slotId,
      copied,
      disabled,
    ]
  );

  const SlotAdditionalDetails = useMemo(() => {
    if (!schema.time_slot_has_description && !schema.time_slot_has_product_link) return null;

    return (
      <DetailsToggle
        dataQaId={`${dataQaId}-toggle-details`}
        defaultOpen={description || productLink?.url || productLink?.display_text}
        wrapperClassName="slots-field__details-toggle"
      >
        {SlotDescription}
        {SlotProductLink}
      </DetailsToggle>
    );
  }, [schema, SlotDescription, SlotProductLink, dataQaId, description, productLink]);

  const FilledSlotsView = useMemo(() => {
    if (signedUpTimeSlots.length === 0) return null;
    return (
      <FilledSlots
        slots={signedUpTimeSlots}
        signedInId={currentUserId}
        isOrganizer
        titleElement={
          <div className="builder__signups-title">
            <Typography variant="label2" size="smaller">
              Confirmed Signups
            </Typography>
            {quantity > 0 && (
              <Typography variant="list2" size="small" color="charcoal">
                {signedUpTimeSlots.length} of {quantity} filled
              </Typography>
            )}
          </div>
        }
      />
    );
  }, [signedUpTimeSlots, currentUserId, quantity]);

  const hasSignups = signedUpTimeSlots.length > 0;

  const gateUnlocked = useSelector(selectGateUnlockedForSheet);
  const slotColor = useSelector(selectSlotColor);
  const gateThisSlot = hasSignups && isInEditMode && !gateUnlocked;
  const {handleUnlock} = useUnlockGate();

  const highlightQuantity = useCallback(() => {
    const quantityField = document.querySelector(`[data-qa-id=${dataQaId}-quantity]`);
    if (quantityField && pristineQuantity) quantityField.focus();
  }, [pristineQuantity, dataQaId]);

  const titleErrors = errors.filter((e) => e.loc?.[4] === 'title');
  return (
    <>
      <div
        className="slots-field__time-slots-wrapper"
        style={{backgroundColor: isMedium ? undefined : slotColor}}
        ref={timeSlotAnimation}
      >
        <GatedContent
          onClick={handleUnlock}
          // biome-ignore lint/style/useConsistentCurlyBraces: existing error
          heading={'This slot already has volunteers. \nWould you like to make changes?'}
          dataQaId={`edit-with-signups-${slotId}`}
          isGated={gateThisSlot}
        >
          <div
            className={cx('slots-field__time-slots', blueprintType, {
              'has-signups': isInEditMode && disabled,
            })}
            style={{backgroundColor: isMedium ? slotColor : undefined}}
            ref={slotsFieldTimesRef}
          >
            {!hideTimeSlots && (
              <TimesField
                id={slotId}
                dataQaId={dataQaId}
                startError={errors.find((e) => e.loc?.[4] === 'start_time')?.msg}
                endError={errors.find((e) => e.loc?.[4] === 'end_time')?.msg}
                startTime={startTime}
                endTime={endTime}
                onChangeStartTime={onChangeStartTime}
                onChangeEndTime={onChangeEndTime}
                defaultStart={defaultStart}
                defaultEnd={defaultEnd}
                disabled={disabled}
              />
            )}
            <TextField
              uuid={slotId}
              value={title || ''}
              onChange={onChangeSignupTitle}
              onBlur={highlightQuantity}
              schema={{
                placeholder: schema.time_slot_title_placeholder ?? 'Add a title',
                name: `${schema.name}__signup-title`,
                // biome-ignore lint/style/useNamingConvention: existing error
                max_chars: 50,
                rich: false,
              }}
              data-qa-id={`${dataQaId}-signup-title`}
              disabled={disabled}
              newOption={newOption}
              errors={titleErrors}
            />
            {QuantityCounter}
            {isMedium && SlotAdditionalDetails}
            {isMedium && FilledSlotsView}
          </div>
          {!disabled && (
            <div className="slots-field__toolbar-wrapper">
              <SlotToolBar
                length={length}
                className="slots-field__toolbar"
                onDeleteItem={onDeleteClick}
                onDuplicateItem={onDuplicate}
                index={slotId}
              />
            </div>
          )}
          {!isMedium && SlotAdditionalDetails}
          {!isMedium && FilledSlotsView}
        </GatedContent>
      </div>
      {warning ? (
        <Typography color="aquamarine" variant="paragraph4" className="slots-field__warning">
          {warning}
        </Typography>
      ) : null}
      {disabled && removeCallback !== null && (
        <RemoveActiveSlotModal cancel={() => setRemoveCallback(null)} remove={removeCallback} />
      )}
    </>
  );
};

export const SlotsField = ({
  signupOption,
  onChange,
  currentUserId,
  onHandleProductLinkBlur,
  schema,
  value,
  className,
  errors,
  newOption = false,
  linkPreviews = {},
  'data-qa-id': dataQaId,
  blueprintType,
  allDay,
  isDesktop = false,
}) => {
  const reProcessLinks = useRef(false);
  const {list: slots} = useSortTimes(value);
  const [showAddMultipleSlots, setShowAddMultipleSlots] = useState(false);
  const [showSlotCreationOptionsMenu, setShowSlotCreationOptionsMenu] = useState(false);

  const toggleAddMultipleSlots = useCallback(() => {
    setShowAddMultipleSlots((prev) => !prev);
    setShowSlotCreationOptionsMenu(false);
  }, []);

  const [slotAnimation] = useAutoAnimate({
    duration: 250,
    easing: 'ease-in-out',
  });
  const isPast = useSelector(selectExpired);

  useEffect(() => {
    if (!reProcessLinks.current) return;
    onHandleProductLinkBlur();
    reProcessLinks.current = false;
  }, [onHandleProductLinkBlur]);

  const addAnother = useCallback(() => {
    onChange([...slots, initializeSlot(blueprintType)]);
    setShowSlotCreationOptionsMenu(false);
  }, [onChange, slots, blueprintType]);

  const addMultiples = useCallback(
    (newSlots) => {
      onChange([...slots, ...newSlots]);
    },
    [onChange, slots]
  );

  const onDuplicate = useCallback(
    (idx) => {
      const newSlot = {...slots[idx]};
      onChange([...slots, newSlot]);
      if (entryHasProductLink(newSlot)) {
        reProcessLinks.current = true;
      }
    },
    [onChange, slots]
  );

  const onSlotChange = useCallback(
    (newSlot, idx, saveChanges = true) => {
      const newSlots = [...slots];
      newSlots[idx] = {...newSlots[idx], ...newSlot};
      onChange(newSlots, saveChanges);
    },
    [onChange, slots]
  );

  const handleOnDelete = useCallback(
    (idx) => {
      if (slots.length > 1) {
        const newSlots = [...slots];
        newSlots.splice(idx, 1);
        onChange(newSlots);
      }
    },
    [onChange, slots]
  );

  const AddAnotherSlotButton = useMemo(
    () => (
      <AddButtonWithTitle
        dataQaId={`${dataQaId}-add-new-slot`}
        onClick={addAnother}
        label="Add time window"
        variant="primary"
        size="small"
      />
    ),
    [dataQaId, addAnother]
  );

  return (
    <div
      className={cx('input-wrapper', 'item-field', {
        [className]: !!className,
      })}
    >
      <div className="slots-field__slots" ref={slotAnimation}>
        {slots.map((slot, idx) => (
          <Slot
            key={slot.slot_id}
            slot={slot}
            index={idx}
            // Treat as upcoming if date or start time not defined, or if the slot is in the future
            upcoming={
              !signupOption.date ||
              !slot.start_time ||
              signupOptionIsAfterToday(signupOption) ||
              (signupOptionIsToday(signupOption) && !slotElapsed(slot))
            }
            currentUserId={currentUserId}
            length={slots.length}
            dataQaId={`${dataQaId}-slot-${idx}`}
            copied={slot.copied}
            onChange={(newSlot, saveChanges = true) => onSlotChange(newSlot, idx, saveChanges)}
            onDuplicate={() => onDuplicate(idx)}
            onHandleProductLinkBlur={onHandleProductLinkBlur}
            onDelete={() => handleOnDelete(idx)}
            schema={schema}
            newOption={newOption || signupOption.new}
            errors={errors.filter((e) => e.loc?.[3] === slot.position_index)}
            blueprintType={blueprintType}
            allDay={allDay}
            linkPreview={linkPreviews[slot.slot_id]}
            disabled={isPast}
            isDesktop={isDesktop}
          />
        ))}
      </div>
      {!signupOptionIsBeforeToday(signupOption) && (
        <>
          <div className="slots-field__add-another">
            {allDay ? (
              // All-day option; hide ability to create multiple slots with time
              AddAnotherSlotButton
            ) : (
              // Option with time slots on; show both slot creation options
              <Dropdown
                buttonQaIdModifier="add-another-slot-trigger"
                data-qa-id={`${dataQaId}-add-new-slot`}
                open={showSlotCreationOptionsMenu}
                setOpen={setShowSlotCreationOptionsMenu}
                trigger={AddAnotherSlotButton}
                content={
                  <>
                    <div className="dropdown__menu-item" key="create-multiple-days">
                      <Button
                        variant="transparent"
                        size="small"
                        fullWidth
                        className="slot-field__add-new-slot__option"
                        data-qa-id="add-single-slot"
                        onClick={addAnother}
                      >
                        <Typography variant="paragraph2">Add a single signup</Typography>
                      </Button>
                    </div>
                    <div className="dropdown__menu-item" key="copy-slots-to-empty-dates">
                      <Button
                        data-qa-id="add-multiple-slot"
                        variant="transparent"
                        className="slot-field__add-new-slot__option"
                        size="small"
                        fullWidth
                        onClick={toggleAddMultipleSlots}
                      >
                        <Typography variant="paragraph2">Add multiple signups with time</Typography>
                      </Button>
                    </div>
                  </>
                }
              />
            )}
          </div>
          {showAddMultipleSlots && (
            <AddMultipleSlots onClose={toggleAddMultipleSlots} onSubmit={addMultiples} />
          )}
        </>
      )}
    </div>
  );
};
